import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ArrowLeft,
  CalendarDays,
  Calculator,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  PackageCheck,
  Pencil,
  Phone,
  Plus,
  Printer,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TenantSummary } from '@forq/contracts';
import {
  allowedServiceTransitions,
  calculateServiceEstimateLineNet,
  calculateServiceEstimateTotals,
  type PlannableServiceOrder,
  type ServiceApprovalChannel,
  type ServiceEstimateLine,
  type ServiceEstimateLineKind,
  type ServiceExecutionMode,
  type ServiceGateway,
  type ServiceJobRecord,
  type ServiceKind,
  type ServiceListBucket,
  type ServiceMaterialLine,
  type ServiceOverview,
  type ServicePage as ServiceResultPage,
  type ServicePrintRecord,
  type ServiceStatus,
  type ServiceTechnicianOption,
  type UvvDueRecord,
} from '@forq/service-data';
import { EmptyState, SearchSelect, confirmDiscard } from '@forq/ui';

function money(value: number): string {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

const estimateKindLabels: Readonly<Record<ServiceEstimateLineKind, string>> = {
  labour: 'Arbeitszeit', travel: 'Anfahrt', part: 'Ersatzteil', service: 'Dienstleistung', text: 'Text',
};
type EstimateDraftLine = Omit<ServiceEstimateLine, 'netAmount'>;

interface ServicePageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly gateway: ServiceGateway;
  /** Öffnet den Kostenvoranschlag als druckbaren FTW-Beleg (Render liegt in der App-Schicht). */
  readonly onPrintEstimate?: (job: ServiceJobRecord) => void;
}

type ServiceView = 'worklist' | 'planner' | 'uvv';

const emptyOverview: ServiceOverview = {
  planningCount: 0,
  issuedCount: 0,
  paperReturnedCount: 0,
  approvalPendingCount: 0,
  billingReadyCount: 0,
};
const emptyPage: ServiceResultPage = { items: [], total: 0, page: 1, pageSize: 50 };

const statusLabels: Record<ServiceStatus, string> = {
  new: 'Neu',
  planned: 'Geplant',
  printed: 'Papierauftrag erstellt',
  issued: 'An Techniker ausgegeben',
  'paper-returned': 'Unterlagen zurück',
  reviewed: 'Rückmeldung geprüft',
  'billing-ready': 'Bereit zur Abrechnung',
  completed: 'Abgeschlossen',
  cancelled: 'Storniert',
  'pickup-planned': 'Abholung geplant',
  received: 'Maschine eingegangen',
  diagnosis: 'Diagnose',
  'estimate-ready': 'Kostenschätzung fertig',
  'approval-pending': 'Kundenfreigabe offen',
  approved: 'Reparatur freigegeben',
  declined: 'Reparatur abgelehnt',
  repairing: 'Reparatur in Arbeit',
  'return-planned': 'Rücklieferung geplant',
  returned: 'Maschine zurückgeliefert',
};

const kindLabels: Record<ServiceKind, string> = {
  repair: 'Reparatur',
  maintenance: 'Wartung',
  uvv: 'UVV-Prüfung',
};

const modeLabels: Record<ServiceExecutionMode, string> = {
  field: 'Außendienst',
  workshop: 'Werkstatt',
};

const bucketTabs: readonly { readonly id: ServiceListBucket; readonly label: string }[] = [
  { id: 'open', label: 'Offene Aufträge' },
  { id: 'planning', label: 'Planung offen' },
  { id: 'issued', label: 'Ausgegeben' },
  { id: 'returned', label: 'Unterlagen zurück' },
  { id: 'billing', label: 'Bereit zur Rechnung' },
  { id: 'completed', label: 'Abgeschlossen' },
  { id: 'all', label: 'Alle' },
];

function formatDateTime(value: string | null): string {
  if (!value) return '–';
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

// Serviceplaner-Datumshilfen. Rein lokal gerechnet (kein toISOString/UTC), damit die Woche nicht an
// der Zeitzone verrutscht. Ersetzt die frueher fest verdrahtete Woche „13.-17.07.".
const PLANNER_WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'] as const;
function isoDateLocal(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function mondayOfWeek(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  const offset = (date.getDay() + 6) % 7; // Montag = 0
  date.setDate(date.getDate() - offset);
  return isoDateLocal(date);
}
function shiftIsoDate(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return isoDateLocal(date);
}
function plannerDayLabel(iso: string): string {
  const date = new Date(`${iso}T00:00:00`);
  return `${PLANNER_WEEKDAYS[date.getDay()]} ${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.`;
}
function plannerDateLabel(iso: string): string {
  return new Intl.DateTimeFormat('de-DE').format(new Date(`${iso}T00:00:00`));
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Die Service-Daten konnten nicht verarbeitet werden.';
}

function nextOperationalStatus(job: ServiceJobRecord): ServiceStatus | null {
  if (job.executionMode === 'field') {
    const map: Partial<Record<ServiceStatus, ServiceStatus>> = {
      printed: 'issued',
      'paper-returned': 'reviewed',
      reviewed: 'billing-ready',
      'billing-ready': 'completed',
    };
    return map[job.status] ?? null;
  }
  const map: Partial<Record<ServiceStatus, ServiceStatus>> = {
    new: 'pickup-planned',
    'pickup-planned': 'received',
    received: 'diagnosis',
    diagnosis: 'estimate-ready',
    'estimate-ready': 'approval-pending',
    approved: 'repairing',
    declined: 'return-planned',
    repairing: 'return-planned',
    'return-planned': 'returned',
    returned: 'billing-ready',
    'billing-ready': 'completed',
  };
  return map[job.status] ?? null;
}

function nextActionLabel(status: ServiceStatus): string {
  const labels: Partial<Record<ServiceStatus, string>> = {
    printed: 'An Techniker ausgeben',
    'paper-returned': 'Rückmeldung prüfen',
    reviewed: 'Zur Abrechnung freigeben',
    'billing-ready': 'Auftrag abschließen',
    new: 'Abholung planen',
    'pickup-planned': 'Maschineneingang bestätigen',
    received: 'Diagnose beginnen',
    diagnosis: 'Kostenschätzung fertigstellen',
    'estimate-ready': 'Kundenfreigabe anfragen',
    approved: 'Reparatur beginnen',
    declined: 'Rücklieferung planen',
    repairing: 'Rücklieferung planen',
    'return-planned': 'Rücklieferung bestätigen',
    returned: 'Zur Abrechnung freigeben',
  };
  return labels[status] ?? 'Weiterbearbeiten';
}

function parseMaterialLines(value: string): readonly ServiceMaterialLine[] {
  return value.split('\n').map((line) => {
    const [articleNumber = '', description = '', quantity = '1', unit = 'Stück'] = line.split('|').map((part) => part.trim());
    return { articleNumber, description, quantity: Number(quantity.replace(',', '.')) || 1, unit: unit || 'Stück' };
  }).filter((line) => line.description);
}

function ServicePrintSheet({ job, document }: { readonly job: ServiceJobRecord; readonly document: ServicePrintRecord }) {
  const snapshot = document.snapshot;
  return (
    <article className="service-print-sheet" aria-label="Druckbarer Serviceauftrag">
      <header>
        <div>
          <strong>Fördertechnik Werl GmbH</strong>
          <span>Technik für Lasten. Lösungen für Menschen.</span>
        </div>
        <div>
          <h1>{snapshot.executionMode === 'workshop' ? 'Werkstattauftrag' : 'Serviceauftrag'}</h1>
          <strong>{document.documentNumber} · Version {document.version}</strong>
          <span>Vorgang {snapshot.orderNumber}</span>
        </div>
      </header>

      <section className="service-print-sheet__meta">
        <div><span>Kunde</span><strong>{snapshot.customerNumber} · {snapshot.customerName}</strong><small>{snapshot.contactName} · {snapshot.contactPhone}</small></div>
        <div><span>Einsatz-/Abholadresse</span><strong>{snapshot.address.street}</strong><small>{snapshot.address.postalCode} {snapshot.address.city}</small></div>
        <div><span>Maschine</span><strong>{snapshot.machineLabel || 'Gerät noch nicht bekannt'}</strong><small>{snapshot.machineNumber || 'ohne Erfassungsnummer'} · S/N {snapshot.serialNumber || '–'}</small></div>
        <div><span>Termin / Techniker</span><strong>{formatDateTime(snapshot.scheduledStart)}</strong><small>{snapshot.technicianName || 'noch nicht zugewiesen'}</small></div>
      </section>

      <section className="service-print-sheet__request">
        <h2>Arbeitsauftrag / Kundenmeldung</h2>
        <p>{snapshot.workDescription}</p>
        <div className="service-print-sheet__checks">
          <span className={snapshot.kind === 'repair' ? 'is-checked' : ''}>□ Reparatur</span>
          <span className={snapshot.kind === 'maintenance' ? 'is-checked' : ''}>□ Wartung</span>
          <span className={snapshot.kind === 'uvv' ? 'is-checked' : ''}>□ UVV-Prüfung</span>
          <span>□ Folgeauftrag erforderlich</span>
          <span>□ Ersatzteile erforderlich</span>
        </div>
      </section>

      <section className="service-print-sheet__times">
        <div><span>Ankunft</span><i /></div><div><span>Abfahrt</span><i /></div><div><span>Fahrtzeit</span><i /></div><div><span>Arbeitszeit</span><i /></div><div><span>Kilometer</span><i /></div>
      </section>

      <section className="service-print-sheet__writing">
        <h2>Durchgeführte Arbeiten / Feststellungen</h2>
        {Array.from({ length: 8 }, (_, index) => <div key={index} />)}
      </section>

      <section className="service-print-sheet__material">
        <h2>Verwendete Ersatzteile und Materialien</h2>
        <div className="service-print-sheet__table-head"><span>Artikelnummer</span><span>Bezeichnung</span><span>Menge</span><span>Einheit</span></div>
        {Array.from({ length: 6 }, (_, index) => <div className="service-print-sheet__table-row" key={index}><span /><span /><span /><span /></div>)}
      </section>

      <section className="service-print-sheet__result">
        <span>□ Auftrag vollständig erledigt</span>
        <span>□ Auftrag nicht vollständig erledigt</span>
        <span>□ Kunde über Mängel / Folgetermin informiert</span>
        <span>□ Maschine außer Betrieb</span>
      </section>

      <section className="service-print-sheet__signatures">
        <div><i /><span>Ort, Datum / Unterschrift Kunde</span></div>
        <div><i /><span>Unterschrift Servicetechniker</span></div>
      </section>

      <footer>
        <strong>Nach Durchführung bitte vollständig ausgefüllt an Serviceleitung / Verwaltung zurückgeben.</strong>
        <span>{job.serviceNumber} · erzeugt {formatDateTime(document.createdAt)}</span>
      </footer>
    </article>
  );
}

// Der Planungs-Dialog speist sich aus einem bestehenden Job ODER aus einem noch ungeplanten
// service-Vorgang. Ein gemeinsamer Kontext haelt beides zusammen: serviceJobId=null bedeutet
// „neuer Job aus diesem Vorgang" (planJob legt ihn dann an).
interface PlanContext {
  readonly serviceJobId: string | null;
  readonly orderId: string;
  readonly headerLabel: string;
  readonly executionMode: ServiceExecutionMode;
  readonly kind: ServiceKind;
  readonly priority: ServiceJobRecord['priority'];
  readonly scheduledStart: string | null;
  readonly scheduledEnd: string | null;
  readonly technicianName: string;
  readonly workDescription: string;
  readonly address: ServiceJobRecord['address'];
  readonly internalNotes: string;
}
function planContextFromJob(job: ServiceJobRecord): PlanContext {
  return {
    serviceJobId: job.id, orderId: job.orderId, headerLabel: job.serviceNumber,
    executionMode: job.executionMode, kind: job.kind, priority: job.priority,
    scheduledStart: job.scheduledStart, scheduledEnd: job.scheduledEnd,
    technicianName: job.technicianName, workDescription: job.workDescription,
    address: job.address, internalNotes: job.internalNotes,
  };
}
function planContextFromOrder(order: PlannableServiceOrder): PlanContext {
  return {
    serviceJobId: null, orderId: order.orderId, headerLabel: order.orderNumber,
    executionMode: 'field', kind: 'repair', priority: 'normal',
    scheduledStart: null, scheduledEnd: null,
    technicianName: '', workDescription: order.title || `Serviceauftrag ${order.orderNumber}`,
    address: order.address, internalNotes: '',
  };
}

export function ServicePage({ tenant, mode, permissions, enabledFeatureIds, gateway, onPrintEstimate }: ServicePageProps) {
  const [view, setView] = useState<ServiceView>('worklist');
  const [plannerWeekStart, setPlannerWeekStart] = useState<string>(() => mondayOfWeek(isoDateLocal(new Date())));
  const [overview, setOverview] = useState<ServiceOverview>(emptyOverview);
  const [page, setPage] = useState<ServiceResultPage>(emptyPage);
  const [bucket, setBucket] = useState<ServiceListBucket>('open');
  const [executionMode, setExecutionMode] = useState<ServiceExecutionMode | 'all'>('all');
  const [kind, setKind] = useState<ServiceKind | 'all'>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ServiceJobRecord | null>(null);
  const [technicians, setTechnicians] = useState<readonly ServiceTechnicianOption[]>([]);
  const [plannableOrders, setPlannableOrders] = useState<readonly PlannableServiceOrder[]>([]);
  const [uvvDue, setUvvDue] = useState<readonly UvvDueRecord[]>([]);
  const [uvvLoading, setUvvLoading] = useState(false);
  const [planContext, setPlanContext] = useState<PlanContext | null>(null);
  const [returnEditor, setReturnEditor] = useState<ServiceJobRecord | null>(null);
  const [approvalEditor, setApprovalEditor] = useState<ServiceJobRecord | null>(null);
  const [estimateEditor, setEstimateEditor] = useState<ServiceJobRecord | null>(null);
  const [printPreview, setPrintPreview] = useState<{ readonly job: ServiceJobRecord; readonly document: ServicePrintRecord } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canPlan = permissions.has('service.plan') && enabledFeatureIds.includes('service.planning');
  const canPrint = permissions.has('service.print') && enabledFeatureIds.includes('service.print');
  const canCaptureReturn = permissions.has('service.return.capture') && enabledFeatureIds.includes('service.paper-return');
  const canApprove = permissions.has('service.approval.manage') && enabledFeatureIds.includes('service.approval');
  const canTransition = permissions.has('service.transition');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextOverview, nextPage, nextTechnicians, nextPlannable] = await Promise.all([
        gateway.getOverview(tenant.id),
        gateway.searchJobs({ tenantId: tenant.id, bucket, executionMode, kind, query, pageSize: 100 }),
        gateway.listTechnicians(tenant.id),
        gateway.listPlannableOrders(tenant.id),
      ]);
      setOverview(nextOverview);
      setPage(nextPage);
      setTechnicians(nextTechnicians);
      setPlannableOrders(nextPlannable);
      setSelected((current) => current
        ? nextPage.items.find((job) => job.id === current.id) ?? nextPage.items[0] ?? null
        : nextPage.items[0] ?? null);
    } catch (loadError) {
      setError(messageOf(loadError));
    } finally {
      setLoading(false);
    }
  }, [bucket, executionMode, gateway, kind, query, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  // UVV-Fälligkeiten getrennt laden (hängen nicht an den Arbeitslisten-Filtern).
  const loadUvv = useCallback(async () => {
    setUvvLoading(true);
    try {
      setUvvDue(await gateway.listUvvDue(tenant.id));
    } catch {
      setUvvDue([]);
    } finally {
      setUvvLoading(false);
    }
  }, [gateway, tenant.id]);
  useEffect(() => { void loadUvv(); }, [loadUvv]);

  useEffect(() => {
    setSelected(null);
    setPlanContext(null);
    setReturnEditor(null);
    setApprovalEditor(null);
    setPrintPreview(null);
  }, [tenant.id]);

  const refreshJob = useCallback(async (serviceJobId: string) => {
    const job = await gateway.getJob(tenant.id, serviceJobId);
    setSelected(job);
    await load();
    return job;
  }, [gateway, load, tenant.id]);

  const savePlan = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!planContext) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const saved = await gateway.planJob({
        tenantId: tenant.id,
        serviceJobId: planContext.serviceJobId ?? undefined,
        orderId: planContext.orderId,
        executionMode: String(form.get('executionMode')) as ServiceExecutionMode,
        kind: String(form.get('kind')) as ServiceKind,
        priority: String(form.get('priority')) as ServiceJobRecord['priority'],
        scheduledStart: String(form.get('scheduledStart') ?? '') || null,
        scheduledEnd: String(form.get('scheduledEnd') ?? '') || null,
        technicianName: String(form.get('technicianName') ?? ''),
        workDescription: String(form.get('workDescription') ?? ''),
        serviceAddress: {
          companyName: String(form.get('companyName') ?? ''),
          contactName: String(form.get('contactName') ?? ''),
          street: String(form.get('street') ?? ''),
          postalCode: String(form.get('postalCode') ?? ''),
          city: String(form.get('city') ?? ''),
          countryCode: 'DE',
        },
        internalNotes: String(form.get('internalNotes') ?? ''),
      });
      setPlanContext(null);
      setSelected(saved);
      await load();
    } catch (saveError) {
      setError(messageOf(saveError));
    } finally {
      setBusy(false);
    }
  }, [gateway, load, planContext, tenant.id]);

  const generatePrint = useCallback(async (job: ServiceJobRecord) => {
    setBusy(true);
    setError(null);
    try {
      const document = await gateway.createPrint({ tenantId: tenant.id, serviceJobId: job.id });
      const refreshed = await gateway.getJob(tenant.id, job.id);
      setPrintPreview({ job: refreshed, document });
      setSelected(refreshed);
      await load();
    } catch (printError) {
      setError(messageOf(printError));
    } finally {
      setBusy(false);
    }
  }, [gateway, load, tenant.id]);

  const printDocument = useCallback(async () => {
    if (!printPreview) return;
    window.print();
    try {
      const marked = await gateway.markPrintAsPrinted({
        tenantId: tenant.id,
        serviceJobId: printPreview.job.id,
        printDocumentId: printPreview.document.id,
      });
      setPrintPreview((current) => current ? { ...current, document: marked } : current);
      await refreshJob(printPreview.job.id);
    } catch (printError) {
      setError(messageOf(printError));
    }
  }, [gateway, printPreview, refreshJob, tenant.id]);

  const captureReturn = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!returnEditor) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const saved = await gateway.capturePaperReturn({
        tenantId: tenant.id,
        serviceJobId: returnEditor.id,
        printDocumentId: returnEditor.latestPrint?.id,
        technicianName: String(form.get('technicianName') ?? ''),
        arrivalTime: String(form.get('arrivalTime') ?? ''),
        departureTime: String(form.get('departureTime') ?? ''),
        travelHours: Number(form.get('travelHours') ?? 0),
        workHours: Number(form.get('workHours') ?? 0),
        kilometers: Number(form.get('kilometers') ?? 0),
        workPerformed: String(form.get('workPerformed') ?? ''),
        materials: parseMaterialLines(String(form.get('materials') ?? '')),
        completed: form.get('completed') === 'on',
        followUpRequired: form.get('followUpRequired') === 'on',
        followUpNotes: String(form.get('followUpNotes') ?? ''),
        customerName: String(form.get('customerName') ?? ''),
        customerSignatureOnPaper: form.get('customerSignatureOnPaper') === 'on',
        officeNotes: String(form.get('officeNotes') ?? ''),
      });
      setReturnEditor(null);
      setSelected(saved);
      setBucket('returned');
      await load();
    } catch (returnError) {
      setError(messageOf(returnError));
    } finally {
      setBusy(false);
    }
  }, [gateway, load, returnEditor, tenant.id]);

  const saveApproval = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!approvalEditor) return;
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const saved = await gateway.setApproval({
        tenantId: tenant.id,
        serviceJobId: approvalEditor.id,
        status: String(form.get('status')) as 'pending' | 'approved' | 'declined',
        approvedAmount: Number(form.get('approvedAmount') ?? 0) || null,
        decidedByName: String(form.get('decidedByName') ?? ''),
        channel: String(form.get('channel')) as ServiceApprovalChannel,
        notes: String(form.get('notes') ?? ''),
      });
      setApprovalEditor(null);
      setSelected(saved);
      await load();
    } catch (approvalError) {
      setError(messageOf(approvalError));
    } finally {
      setBusy(false);
    }
  }, [approvalEditor, gateway, load, tenant.id]);

  const saveEstimate = useCallback(async (lines: readonly EstimateDraftLine[], note: string) => {
    if (!estimateEditor) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await gateway.saveEstimate({ tenantId: tenant.id, serviceJobId: estimateEditor.id, lines, note });
      setEstimateEditor(null);
      setSelected(saved);
      await load();
    } catch (estimateError) {
      setError(messageOf(estimateError));
    } finally {
      setBusy(false);
    }
  }, [estimateEditor, gateway, load, tenant.id]);

  const transition = useCallback(async (job: ServiceJobRecord, toStatus: ServiceStatus) => {
    setBusy(true);
    setError(null);
    try {
      const saved = await gateway.transitionJob({ tenantId: tenant.id, serviceJobId: job.id, toStatus });
      setSelected(saved);
      await load();
    } catch (transitionError) {
      setError(messageOf(transitionError));
    } finally {
      setBusy(false);
    }
  }, [gateway, load, tenant.id]);

  // Mo–Fr der aktuell gewaehlten Woche, aus plannerWeekStart abgeleitet (nicht mehr fest verdrahtet).
  const plannerDays = useMemo(
    () => Array.from({ length: 5 }, (_, index) => {
      const iso = shiftIsoDate(plannerWeekStart, index);
      return { iso, label: plannerDayLabel(iso) };
    }),
    [plannerWeekStart],
  );

  if (!enabledFeatureIds.includes('service.worklist')) {
    return <EmptyState eyebrow="Service" title="Die Service-Arbeitsliste ist für diese Rolle deaktiviert." description="Die Funktion kann in der Administration freigeschaltet werden." />;
  }

  return (
    <div className="service-page">
      <header className="service-page__header">
        <div>
          <span className="service-page__eyebrow">FORQ Service · {mode === 'demo' ? 'Demo' : 'Supabase'}</span>
          <h1>Service & Werkstatt</h1>
          <p>Papieraufträge planen, ausgeben, zurücknehmen und zur Abrechnung freigeben.</p>
        </div>
        <div className="service-page__header-actions">
          <button type="button" className={view === 'worklist' ? 'is-active' : ''} onClick={() => setView('worklist')}><ClipboardCheck size={14} /> Arbeitsliste</button>
          <button type="button" className={view === 'planner' ? 'is-active' : ''} onClick={() => setView('planner')}><CalendarDays size={14} /> Serviceplaner</button>
          <button type="button" className={view === 'uvv' ? 'is-active' : ''} onClick={() => setView('uvv')}><ShieldCheck size={14} /> UVV-Prüfungen{uvvDue.length > 0 ? <span className="service-page__badge">{uvvDue.length}</span> : null}</button>
          <Link className="is-primary" to="/vorgaenge"><Plus size={14} /> Serviceauftrag anlegen</Link>
        </div>
      </header>

      {error && !planContext && !returnEditor && !approvalEditor && !estimateEditor ? <div className="service-error" role="alert"><span>{error}</span><button type="button" onClick={() => setError(null)}>Schließen</button></div> : null}

      {view === 'worklist' ? (
        <>
          <section className="service-status-strip">
            <article><span>Planung offen</span><strong>{overview.planningCount}</strong></article>
            <article><span>Ausgegeben</span><strong>{overview.issuedCount}</strong></article>
            <article><span>Unterlagen zurück</span><strong>{overview.paperReturnedCount}</strong></article>
            <article><span>Freigaben offen</span><strong>{overview.approvalPendingCount}</strong></article>
            <article><span>Bereit zur Rechnung</span><strong>{overview.billingReadyCount}</strong></article>
          </section>

          {plannableOrders.length > 0 ? (
            <section className="service-plannable">
              <header><span>Zu planen</span><strong>{plannableOrders.length} Serviceauftrag{plannableOrders.length === 1 ? '' : 'e'} ohne Werkstatt-Termin</strong></header>
              <div className="service-plannable__list">
                {plannableOrders.map((order) => (
                  <article key={order.orderId} className="service-plannable__item">
                    <div>
                      <strong>{order.orderNumber}</strong>
                      <span>{order.title || 'Serviceauftrag'}</span>
                      <small>{order.customerName}{order.machineLabel ? ` · ${order.machineLabel}` : ''}</small>
                    </div>
                    {canPlan ? (
                      <button type="button" className="is-primary" onClick={() => setPlanContext(planContextFromOrder(order))}>
                        <CalendarDays size={14} /> Einplanen
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <nav className="service-filter-tabs" aria-label="Service-Statusfilter">
            {bucketTabs.map((tab) => <button type="button" key={tab.id} className={bucket === tab.id ? 'is-active' : ''} onClick={() => setBucket(tab.id)}>{tab.label}</button>)}
          </nav>

          <div className="service-toolbar">
            <label><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Auftrag, Kunde, Maschine oder Techniker suchen" /></label>
            <SearchSelect value={executionMode} onChange={(next) => setExecutionMode(next as ServiceExecutionMode | 'all')} options={[{ value: 'all', label: 'Außen & Werkstatt' }, { value: 'field', label: 'Außendienst' }, { value: 'workshop', label: 'Werkstatt' }]} />
            <SearchSelect value={kind} onChange={(next) => setKind(next as ServiceKind | 'all')} options={[{ value: 'all', label: 'Alle Auftragsarten' }, ...Object.entries(kindLabels).map(([value, label]) => ({ value, label }))]} />
            {/* Der frühere Knopf „Filter" hatte nie eine Wirkung -- gefiltert wird über Suche,
                Ausführungsart und Auftragsart daneben. Entfernt statt weiter vorgetäuscht. */}
            <button type="button" onClick={() => void load()} disabled={loading}><RefreshCw size={14} /> Aktualisieren</button>
            <span>{page.total} Datensätze</span>
          </div>

          <div className="service-workspace">
            <section className="service-list-panel">
              <div className="service-table">
                <div className="service-table__head"><span>Auftrag</span><span>Art</span><span>Kunde</span><span>Maschine</span><span>Termin</span><span>Techniker</span><span>Status</span></div>
                {loading ? <div className="service-table__empty">Serviceaufträge werden geladen …</div> : page.items.length === 0 ? <div className="service-table__empty">Keine Serviceaufträge für diesen Filter.</div> : page.items.map((job) => (
                  <button type="button" key={job.id} className={`service-table__row ${selected?.id === job.id ? 'is-selected' : ''}`} onClick={() => setSelected(job)}>
                    <span><strong>{job.serviceNumber}</strong><small>{job.orderNumber}</small></span>
                    <span>{modeLabels[job.executionMode]}<small>{kindLabels[job.kind]}</small></span>
                    <span><strong>{job.customerName}</strong><small>{job.contactName}</small></span>
                    <span><strong>{job.machineLabel || 'Gerät unbekannt'}</strong><small>{job.machineNumber || 'ohne Nummer'}</small></span>
                    <span>{formatDateTime(job.scheduledStart)}</span>
                    <span>{job.technicianName || 'nicht geplant'}</span>
                    <span><i className={`is-${job.bucket}`} />{statusLabels[job.status]}</span>
                  </button>
                ))}
              </div>
            </section>

            <aside className="service-detail-panel">
              {selected ? (
                <>
                  <div className="service-detail-panel__heading">
                    <div><span>{selected.serviceNumber} · {selected.orderNumber}</span><h2>{kindLabels[selected.kind]} · {selected.machineLabel || 'Gerät unbekannt'}</h2><p>{selected.customerName}</p></div>
                    <em className={`is-${selected.bucket}`}>{statusLabels[selected.status]}</em>
                  </div>
                  {/* Hier stand eine Reiterleiste „Übersicht / Papierauftrag / Rückmeldung / Verlauf",
                      deren vier Knöpfe keine Klickbehandlung hatten -- es gibt nur diese eine Ansicht,
                      und einen Verlauf gibt es gar nicht. Entfernt, bis die Reiter wirklich existieren. */}
                  <dl>
                    <div><dt>Ausführung</dt><dd>{modeLabels[selected.executionMode]}</dd></div>
                    <div><dt>Termin</dt><dd>{formatDateTime(selected.scheduledStart)}</dd></div>
                    <div><dt>Techniker</dt><dd>{selected.technicianName || 'nicht zugewiesen'}</dd></div>
                    <div><dt>Adresse</dt><dd>{selected.address.street}, {selected.address.postalCode} {selected.address.city}</dd></div>
                  </dl>
                  <section className="service-detail-panel__description"><span>Arbeitsauftrag / Kundenmeldung</span><strong>{selected.workDescription}</strong></section>

                  {selected.estimate ? (
                    <section className="service-estimate-summary">
                      <div>
                        <span>Kostenvoranschlag {selected.estimate.estimateNumber}</span>
                        <strong>{money(selected.estimate.grossTotal)}</strong>
                        <small>{selected.estimate.lines.length} Position{selected.estimate.lines.length === 1 ? '' : 'en'} · netto {money(selected.estimate.netTotal)} zzgl. USt.</small>
                      </div>
                      {onPrintEstimate ? <button type="button" className="service-estimate-print" onClick={() => onPrintEstimate(selected)}><Printer size={14} /> Drucken</button> : null}
                    </section>
                  ) : null}

                  {selected.executionMode === 'workshop' ? (
                    <section className="service-approval-summary">
                      <span>Reparaturfreigabe</span>
                      <strong>{selected.approval.status === 'pending' ? 'Entscheidung offen' : selected.approval.status === 'approved' ? 'Freigegeben' : selected.approval.status === 'declined' ? 'Abgelehnt' : 'Noch nicht erforderlich'}</strong>
                      <small>{selected.approval.approvedAmount != null ? `${selected.approval.approvedAmount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} · ` : ''}{selected.approval.notes}</small>
                    </section>
                  ) : null}

                  {selected.paperReturn ? (
                    <section className="service-return-summary">
                      <span>Zurückgegebener Papierauftrag</span>
                      <strong>{selected.paperReturn.workPerformed}</strong>
                      <small>{selected.paperReturn.workHours} h Arbeit · {selected.paperReturn.travelHours} h Fahrt · {selected.paperReturn.kilometers} km · Unterschrift {selected.paperReturn.customerSignatureOnPaper ? 'vorhanden' : 'fehlt'}</small>
                    </section>
                  ) : null}

                  <div className="service-detail-panel__actions">
                    {canPlan ? <button type="button" onClick={() => setPlanContext(planContextFromJob(selected))}><Pencil size={14} /> Planung</button> : null}
                    {canPrint ? <button type="button" className="is-primary" onClick={() => void generatePrint(selected)} disabled={busy}><Printer size={14} /> {selected.latestPrint ? 'Neu drucken' : 'Papierauftrag'}</button> : null}
                    {canCaptureReturn && ['issued', 'printed'].includes(selected.status) ? <button type="button" onClick={() => setReturnEditor(selected)}><ClipboardCheck size={14} /> Unterlagen zurück</button> : null}
                    {canPlan && selected.executionMode === 'workshop' && ['received', 'diagnosis', 'estimate-ready', 'approval-pending'].includes(selected.status) ? <button type="button" onClick={() => setEstimateEditor(selected)}><Calculator size={14} /> {selected.estimate ? 'Kostenvoranschlag ändern' : 'Kostenvoranschlag'}</button> : null}
                    {canApprove && selected.executionMode === 'workshop' && ['estimate-ready', 'approval-pending'].includes(selected.status) ? <button type="button" onClick={() => setApprovalEditor(selected)}><CheckCircle2 size={14} /> Freigabe</button> : null}
                  </div>

                  {canTransition && nextOperationalStatus(selected) && allowedServiceTransitions(selected.executionMode, selected.status).includes(nextOperationalStatus(selected) as ServiceStatus) ? (
                    <button type="button" className="service-next-action" onClick={() => void transition(selected, nextOperationalStatus(selected) as ServiceStatus)} disabled={busy}>
                      <PackageCheck size={14} /> {nextActionLabel(selected.status)}
                    </button>
                  ) : null}
                </>
              ) : <div className="service-detail-panel__empty">Serviceauftrag auswählen.</div>}
            </aside>
          </div>
        </>
      ) : view === 'planner' ? (
        <section className="service-planner">
          <header><div><span>Serviceplaner</span><h2>{plannerDateLabel(plannerWeekStart)} – {plannerDateLabel(shiftIsoDate(plannerWeekStart, 4))}</h2></div><div><button type="button" aria-label="Vorherige Woche" onClick={() => setPlannerWeekStart((current) => shiftIsoDate(current, -7))}><ChevronLeft size={14} /></button><button type="button" onClick={() => setPlannerWeekStart(mondayOfWeek(isoDateLocal(new Date())))}>Heute</button><button type="button" aria-label="Nächste Woche" onClick={() => setPlannerWeekStart((current) => shiftIsoDate(current, 7))}><ChevronRight size={14} /></button><button type="button" className="is-primary" onClick={() => setView('worklist')}><Plus size={14} /> Einsatz planen</button></div></header>
          <div className="service-planner__body">
            <aside><span>Disposition</span><h3>Ungeplante Aufträge</h3>{plannableOrders.length === 0 ? <p className="service-planner__empty">Keine offenen Serviceaufträge.</p> : plannableOrders.map((order) => (
              <article key={order.orderId}>
                <strong>{order.orderNumber}</strong>
                <small>{order.customerName}{order.machineLabel ? ` · ${order.machineLabel}` : ''}</small>
                {canPlan ? <button type="button" onClick={() => setPlanContext(planContextFromOrder(order))}><CalendarDays size={12} /> Einplanen</button> : null}
              </article>
            ))}</aside>
            <div className="service-planner__grid">
              <div className="service-planner__head"><span>Techniker</span>{plannerDays.map((day) => <span key={day.iso}>{day.label}</span>)}</div>
              {technicians.filter((technician) => technician.active).map((technician) => (
                <div className="service-planner__row" key={technician.id}><strong>{technician.displayName}</strong>{plannerDays.map((day) => {
                  const job = page.items.find((candidate) => candidate.technicianName === technician.displayName && candidate.scheduledStart?.slice(0, 10) === day.iso);
                  return <span key={day.iso}>{job ? <button type="button" onClick={() => { setSelected(job); setView('worklist'); }}><i>{job.serviceNumber}</i><small>{job.customerName}</small></button> : null}</span>;
                })}</div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <UvvDueView
          records={uvvDue}
          loading={uvvLoading}
          onRefresh={() => void loadUvv()}
          onOpenJob={(serviceNumber) => { setQuery(serviceNumber); setBucket('all'); setView('worklist'); }}
        />
      )}

      {planContext ? (
        <div className="service-dialog" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) setPlanContext(null); }}>
          <form className="service-dialog__panel" onSubmit={savePlan}>
            <header><div><span>Serviceplanung</span><h2>{planContext.headerLabel} planen</h2></div><button type="button" onClick={() => setPlanContext(null)}><X size={17} /></button></header>
            <div className="service-form-grid">
              <label>Ausführung<SearchSelect name="executionMode" defaultValue={planContext.executionMode} options={[{ value: 'field', label: 'Außendienst' }, { value: 'workshop', label: 'Werkstatt' }]} /></label>
              <label>Auftragsart<SearchSelect name="kind" defaultValue={planContext.kind} options={Object.entries(kindLabels).map(([value, label]) => ({ value, label }))} /></label>
              <label>Priorität<SearchSelect name="priority" defaultValue={planContext.priority} options={[{ value: 'low', label: 'Niedrig' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'Hoch' }, { value: 'urgent', label: 'Dringend' }]} /></label>
              <label>Beginn<input name="scheduledStart" type="datetime-local" defaultValue={planContext.scheduledStart?.slice(0, 16) ?? ''} /></label>
              <label>Ende<input name="scheduledEnd" type="datetime-local" defaultValue={planContext.scheduledEnd?.slice(0, 16) ?? ''} /></label>
              <label>Techniker<SearchSelect name="technicianName" defaultValue={planContext.technicianName} options={[{ value: '', label: 'Nicht zugewiesen' }, ...technicians.map((technician) => ({ value: technician.displayName, label: technician.displayName }))]} /></label>
              <label className="is-wide">Arbeitsauftrag / Kundenmeldung<textarea name="workDescription" rows={4} defaultValue={planContext.workDescription} required /></label>
              <label>Firma<input name="companyName" defaultValue={planContext.address.companyName} /></label>
              <label>Ansprechpartner<input name="contactName" defaultValue={planContext.address.contactName} /></label>
              <label>Straße<input name="street" defaultValue={planContext.address.street} /></label>
              <label>PLZ<input name="postalCode" defaultValue={planContext.address.postalCode} /></label>
              <label>Ort<input name="city" defaultValue={planContext.address.city} /></label>
              <label className="is-wide">Interne Hinweise<textarea name="internalNotes" rows={3} defaultValue={planContext.internalNotes} /></label>
            </div>
            {error ? <div className="service-dialog__error" role="alert">{error}</div> : null}
            <footer><button type="button" onClick={() => setPlanContext(null)}>Abbrechen</button><button type="submit" className="is-primary" disabled={busy}>{busy ? 'Speichert …' : 'Planung speichern'}</button></footer>
          </form>
        </div>
      ) : null}

      {returnEditor ? (
        <div className="service-dialog" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) setReturnEditor(null); }}>
          <form className="service-dialog__panel is-wide" onSubmit={captureReturn}>
            <header><div><span>Papier-Rückmeldung</span><h2>{returnEditor.serviceNumber} erfassen</h2><p>Die Verwaltung überträgt die Angaben des zurückgegebenen Papierauftrags.</p></div><button type="button" onClick={() => setReturnEditor(null)}><X size={17} /></button></header>
            <div className="service-form-grid service-form-grid--return">
              <label>Techniker<input name="technicianName" defaultValue={returnEditor.technicianName} /></label>
              <label>Ankunft<input name="arrivalTime" type="time" /></label>
              <label>Abfahrt<input name="departureTime" type="time" /></label>
              <label>Fahrtzeit Stunden<input name="travelHours" type="number" min="0" step="0.25" defaultValue="0" /></label>
              <label>Arbeitszeit Stunden<input name="workHours" type="number" min="0" step="0.25" defaultValue="0" /></label>
              <label>Kilometer<input name="kilometers" type="number" min="0" step="1" defaultValue="0" /></label>
              <label className="is-wide">Durchgeführte Arbeiten / Feststellungen<textarea name="workPerformed" rows={5} required /></label>
              <label className="is-wide">Materialien – eine Zeile je Position: Artikelnummer | Bezeichnung | Menge | Einheit<textarea name="materials" rows={4} placeholder="10042 | Hydraulikfilter | 1 | Stück" /></label>
              <label className="service-check"><input name="completed" type="checkbox" /> Auftrag vollständig erledigt</label>
              <label className="service-check"><input name="followUpRequired" type="checkbox" /> Folgeauftrag erforderlich</label>
              <label className="service-check"><input name="customerSignatureOnPaper" type="checkbox" /> Kundenunterschrift auf Papier vorhanden</label>
              <label>Kundenname<input name="customerName" defaultValue={returnEditor.contactName} /></label>
              <label className="is-wide">Hinweise zum Folgeauftrag<textarea name="followUpNotes" rows={3} /></label>
              <label className="is-wide">Vermerk der Verwaltung<textarea name="officeNotes" rows={3} /></label>
            </div>
            {error ? <div className="service-dialog__error" role="alert">{error}</div> : null}
            <footer><button type="button" onClick={() => setReturnEditor(null)}>Abbrechen</button><button type="submit" className="is-primary" disabled={busy}>{busy ? 'Speichert …' : 'Rückmeldung übernehmen'}</button></footer>
          </form>
        </div>
      ) : null}

      {approvalEditor ? (
        <div className="service-dialog" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && confirmDiscard()) setApprovalEditor(null); }}>
          <form className="service-dialog__panel" onSubmit={saveApproval}>
            <header><div><span>Werkstattfreigabe</span><h2>Kundenentscheidung dokumentieren</h2></div><button type="button" onClick={() => setApprovalEditor(null)}><X size={17} /></button></header>
            {approvalEditor.estimate ? (
              <p className="service-dialog__hint">Kostenvoranschlag {approvalEditor.estimate.estimateNumber}: <strong>{money(approvalEditor.estimate.grossTotal)}</strong> — als Freigabebetrag vorgeschlagen.</p>
            ) : (
              <p className="service-dialog__hint">Hinweis: Für diesen Auftrag liegt noch kein Kostenvoranschlag vor.</p>
            )}
            <div className="service-form-grid">
              <label>Entscheidung<SearchSelect name="status" defaultValue={approvalEditor.approval.status === 'not-required' ? 'pending' : approvalEditor.approval.status} options={[{ value: 'pending', label: 'Noch offen' }, { value: 'approved', label: 'Reparatur freigegeben' }, { value: 'declined', label: 'Reparatur abgelehnt' }]} /></label>
              <label>Freigegebener Höchstbetrag<input name="approvedAmount" type="number" min="0" step="0.01" defaultValue={approvalEditor.approval.approvedAmount ?? approvalEditor.estimate?.grossTotal ?? ''} /></label>
              <label>Entscheidung durch<input name="decidedByName" defaultValue={approvalEditor.contactName} /></label>
              <label>Kanal<SearchSelect name="channel" defaultValue={approvalEditor.approval.channel ?? 'phone'} options={[{ value: 'phone', label: 'Telefon' }, { value: 'email', label: 'E-Mail' }, { value: 'written', label: 'Schriftlich' }, { value: 'onsite', label: 'Vor Ort' }]} /></label>
              <label className="is-wide">Notiz zur Freigabe<textarea name="notes" rows={4} defaultValue={approvalEditor.approval.notes} /></label>
            </div>
            {error ? <div className="service-dialog__error" role="alert">{error}</div> : null}
            <footer><button type="button" onClick={() => setApprovalEditor(null)}>Abbrechen</button><button type="submit" className="is-primary" disabled={busy}>Entscheidung speichern</button></footer>
          </form>
        </div>
      ) : null}

      {estimateEditor ? (
        <EstimateEditor job={estimateEditor} busy={busy} error={error} onCancel={() => { if (confirmDiscard()) setEstimateEditor(null); }} onSave={saveEstimate} />
      ) : null}

      {printPreview ? (
        <div className="service-print-preview">
          <header><button type="button" onClick={() => setPrintPreview(null)}><ArrowLeft size={15} /> Zurück zu Service</button><div><span>Druckvorschau</span><strong>{printPreview.document.documentNumber} · Version {printPreview.document.version}</strong></div><button type="button" className="is-primary" onClick={() => void printDocument()}><Printer size={15} /> Drucken</button></header>
          <div className="service-print-preview__canvas"><ServicePrintSheet job={printPreview.job} document={printPreview.document} /></div>
        </div>
      ) : null}
    </div>
  );
}

function EstimateEditor({ job, busy, error, onCancel, onSave }: {
  readonly job: ServiceJobRecord;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSave: (lines: readonly EstimateDraftLine[], note: string) => void;
}) {
  const [lines, setLines] = useState<EstimateDraftLine[]>(
    job.estimate && job.estimate.lines.length > 0
      ? job.estimate.lines.map((line) => ({ kind: line.kind, description: line.description, quantity: line.quantity, unit: line.unit, unitPrice: line.unitPrice, discountPercent: line.discountPercent, vatRate: line.vatRate }))
      : [{ kind: 'labour', description: '', quantity: 1, unit: 'Std', unitPrice: 0, discountPercent: 0, vatRate: 19 }],
  );
  const [note, setNote] = useState(job.estimate?.note ?? '');
  const priced = lines.map((line) => ({ ...line, netAmount: calculateServiceEstimateLineNet(line) }));
  const totals = calculateServiceEstimateTotals(priced);
  const update = (index: number, patch: Partial<EstimateDraftLine>) => setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const removeRow = (index: number) => setLines((current) => (current.length > 1 ? current.filter((_, i) => i !== index) : current));
  const canSave = priced.some((line) => line.description.trim());
  return (
    <div className="service-dialog" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onCancel(); }}>
      <div className="service-dialog__panel service-estimate-panel">
        <header><div><span>Serviceauftrag {job.serviceNumber}</span><h2>Kostenvoranschlag erfassen</h2></div><button type="button" onClick={onCancel}><X size={17} /></button></header>
        <div className="service-estimate-grid">
          <div className="service-estimate-grid__head"><span>Art</span><span>Bezeichnung</span><span>Menge</span><span>Einheit</span><span>Einzelpreis</span><span>USt.</span><span>Netto</span><span aria-hidden="true" /></div>
          {lines.map((line, index) => (
            <div className="service-estimate-grid__row" key={index}>
              <select value={line.kind} onChange={(event) => update(index, { kind: event.target.value as ServiceEstimateLineKind })}>{(Object.keys(estimateKindLabels) as ServiceEstimateLineKind[]).map((kind) => <option key={kind} value={kind}>{estimateKindLabels[kind]}</option>)}</select>
              <input value={line.description} onChange={(event) => update(index, { description: event.target.value })} placeholder="Bezeichnung" />
              <input type="number" min="0" step="0.5" value={line.quantity} onChange={(event) => update(index, { quantity: Number(event.target.value) || 0 })} disabled={line.kind === 'text'} />
              <input value={line.unit} onChange={(event) => update(index, { unit: event.target.value })} disabled={line.kind === 'text'} />
              <input type="number" min="0" step="0.01" value={line.unitPrice} onChange={(event) => update(index, { unitPrice: Number(event.target.value) || 0 })} disabled={line.kind === 'text'} />
              <select value={line.vatRate} onChange={(event) => update(index, { vatRate: Number(event.target.value) })} disabled={line.kind === 'text'}>{[19, 7, 0].map((rate) => <option key={rate} value={rate}>{rate} %</option>)}</select>
              <strong>{money(priced[index].netAmount)}</strong>
              <button type="button" title="Position entfernen" onClick={() => removeRow(index)}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <button type="button" className="service-estimate-add" onClick={() => setLines((current) => [...current, { kind: 'part', description: '', quantity: 1, unit: 'Stk', unitPrice: 0, discountPercent: 0, vatRate: 19 }])}><Plus size={14} /> Position</button>
        <label className="service-estimate-note">Hinweis für den Kunden<textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="z. B. Freibleibend, 30 Tage gültig. Mehrarbeiten nach Aufwand." /></label>
        <div className="service-estimate-total"><span>Netto {money(totals.netTotal)} · USt. {money(totals.vatTotal)}</span><strong>Gesamt {money(totals.grossTotal)}</strong></div>
        {error ? <div className="service-dialog__error" role="alert">{error}</div> : null}
        <footer><button type="button" onClick={onCancel}>Abbrechen</button><button type="button" className="is-primary" disabled={busy || !canSave} onClick={() => onSave(lines, note)}>Kostenvoranschlag speichern</button></footer>
      </div>
    </div>
  );
}

// --- UVV-Fälligkeiten -------------------------------------------------------

const UVV_MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'] as const;
const uvvDueDayMs = 86_400_000;

interface UvvDueGroup {
  readonly key: string;
  readonly label: string;
  readonly tone: 'overdue' | 'future';
  readonly items: readonly UvvDueRecord[];
}

/** ISO-Datum (YYYY-MM-DD) → deutsches Format, ohne Zeitzonen-Verschiebung. */
function formatUvvDate(iso: string | null): string {
  if (!iso) return '–';
  const [year, month, day] = iso.split('-');
  return year && month && day ? `${day}.${month}.${year}` : iso;
}

/** Urgenz-Pille je Zeile relativ zu heute. */
function uvvRowTone(nextUvvDate: string): { readonly tone: 'overdue' | 'soon' | 'ok'; readonly label: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${nextUvvDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return { tone: 'ok', label: '' };
  const days = Math.round((due.getTime() - today.getTime()) / uvvDueDayMs);
  if (days < 0) return { tone: 'overdue', label: `überfällig seit ${Math.abs(days)} Tg.` };
  if (days === 0) return { tone: 'soon', label: 'heute fällig' };
  if (days <= 30) return { tone: 'soon', label: `in ${days} Tg.` };
  return { tone: 'ok', label: `in ${days} Tg.` };
}

function uvvJobKindLabel(kind: string): string {
  if (kind === 'uvv') return 'UVV';
  if (kind === 'repair') return 'Reparatur';
  if (kind === 'maintenance') return 'Wartung';
  return kind;
}

/** Gruppiert nach „Überfällig" (zuerst) und danach je Fälligkeitsmonat, chronologisch. */
function groupUvvDue(records: readonly UvvDueRecord[]): readonly UvvDueGroup[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue: UvvDueRecord[] = [];
  const months = new Map<number, { label: string; items: UvvDueRecord[] }>();
  for (const record of records) {
    const due = new Date(`${record.nextUvvDate}T00:00:00`);
    if (Number.isNaN(due.getTime())) continue;
    if (due.getTime() < today.getTime()) { overdue.push(record); continue; }
    const sort = due.getFullYear() * 100 + due.getMonth();
    const existing = months.get(sort);
    if (existing) existing.items.push(record);
    else months.set(sort, { label: `${UVV_MONTHS_DE[due.getMonth()]} ${due.getFullYear()}`, items: [record] });
  }
  const byDate = (a: UvvDueRecord, b: UvvDueRecord) => a.nextUvvDate.localeCompare(b.nextUvvDate);
  const groups: UvvDueGroup[] = [];
  if (overdue.length > 0) groups.push({ key: 'overdue', label: 'Überfällig', tone: 'overdue', items: [...overdue].sort(byDate) });
  for (const sort of [...months.keys()].sort((a, b) => a - b)) {
    const month = months.get(sort)!;
    groups.push({ key: `m-${sort}`, label: month.label, tone: 'future', items: [...month.items].sort(byDate) });
  }
  return groups;
}

function UvvDueView({ records, loading, onRefresh, onOpenJob }: {
  readonly records: readonly UvvDueRecord[];
  readonly loading: boolean;
  readonly onRefresh: () => void;
  readonly onOpenJob: (serviceNumber: string) => void;
}) {
  const groups = useMemo(() => groupUvvDue(records), [records]);
  return (
    <section className="service-uvv">
      <header className="service-uvv__head">
        <div>
          <span className="service-page__eyebrow">Service · Sicherheit</span>
          <h2>UVV-Prüftermine</h2>
          <p>Fällige Prüfungen nach Monat gruppiert – überfällige zuerst.</p>
        </div>
        <button type="button" onClick={onRefresh} disabled={loading}><RefreshCw size={14} /> Aktualisieren</button>
      </header>
      {records.length === 0 ? (
        <EmptyState eyebrow="UVV" title="Keine Prüftermine hinterlegt." description="Sobald an einer Maschine ein nächstes UVV-Prüfdatum steht – in der Maschinenakte oder automatisch beim Abrechnen einer UVV – erscheint sie hier." />
      ) : (
        <div className="service-uvv__groups">
          {groups.map((group) => (
            <section key={group.key} className={`service-uvv__group is-${group.tone}`}>
              <header className="service-uvv__grouphead"><h3>{group.label}</h3><span>{group.items.length} {group.items.length === 1 ? 'Maschine' : 'Maschinen'}</span></header>
              <div className="service-uvv__list">
                {group.items.map((record) => {
                  const tone = uvvRowTone(record.nextUvvDate);
                  const addressLine = [record.street, [record.postalCode, record.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
                  return (
                    <article key={record.machineId} className="service-uvv__item">
                      <div className="service-uvv__machine">
                        <ShieldCheck size={16} />
                        <div><strong>{record.manufacturer} {record.model}</strong><small>{record.typeName || 'Gerät'} · Nr. {record.machineNumber}</small></div>
                      </div>
                      <div className="service-uvv__customer">
                        <strong>{record.customerName || 'Ohne Kundenzuordnung'}</strong>
                        <small>{addressLine || 'Keine Anschrift hinterlegt'}</small>
                        {record.phone ? <a className="service-uvv__phone" href={`tel:${record.phone.replace(/\s+/g, '')}`}><Phone size={12} /> {record.phone}</a> : null}
                      </div>
                      <div className="service-uvv__dates">
                        <div><span>Nächste Prüfung</span><strong>{formatUvvDate(record.nextUvvDate)}</strong>{tone.label ? <em className={`service-uvv__pill is-${tone.tone}`}>{tone.label}</em> : null}</div>
                        <div><span>Letzte Prüfung</span><strong>{formatUvvDate(record.lastUvvDate)}</strong></div>
                      </div>
                      <div className="service-uvv__last">
                        <span>Zuletzt</span>
                        {record.lastJobId && record.lastJobNumber ? (
                          <button type="button" onClick={() => onOpenJob(record.lastJobNumber)}><ClipboardCheck size={13} /> {record.lastJobNumber}{record.lastJobKind ? ` · ${uvvJobKindLabel(record.lastJobKind)}` : ''}</button>
                        ) : <em className="service-uvv__nolast">Noch kein Auftrag</em>}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
}
